import { useState } from 'react';
import { toast } from 'sonner';
import { Download, Trash2 } from 'lucide-react';
import { useT } from '@/lib/i18n';

const pill =
  'inline-flex h-7 items-center gap-1.5 rounded-md border bg-card px-2.5 text-xs font-semibold transition-colors hover:bg-accent disabled:opacity-50';

/** Deletes the ticked records one by one, after asking. */
export function BulkDelete({
  ids,
  onDone,
  remove,
  noun,
}: {
  ids: string[];
  onDone: () => void;
  remove: (id: string) => Promise<unknown>;
  noun: string;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm(t('Delete {count} {noun}? They go to the recycle bin.', { count: ids.length, noun: t(noun) }))) return;
    setBusy(true);
    let failed = 0;
    for (const id of ids) {
      try {
        await remove(id);
      } catch {
        failed += 1;
      }
    }
    setBusy(false);
    if (failed) toast.error(t('{count} could not be deleted', { count: failed }));
    else toast.success(t('Deleted'));
    onDone();
  };
  return (
    <button type="button" className={`${pill} text-destructive`} onClick={run} disabled={busy}>
      <Trash2 className="h-3.5 w-3.5" />
      {t('Delete')}
    </button>
  );
}

/** Downloads the ticked rows as a CSV the way they appear in the list. */
export function BulkExport<T>({
  rows,
  ids,
  rowKey,
  fields,
  filename,
}: {
  rows: T[];
  ids: string[];
  rowKey: (row: T) => string;
  fields: { header: string; value: (row: T) => string | number | null | undefined }[];
  filename: string;
}) {
  const t = useT();
  const run = () => {
    const picked = rows.filter((row) => ids.includes(rowKey(row)));
    const escape = (value: unknown) => {
      const text = value === null || value === undefined ? '' : String(value);
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const csv = [fields.map((field) => escape(field.header)).join(','), ...picked.map((row) => fields.map((field) => escape(field.value(row))).join(','))].join('\n');
    // A BOM so Excel opens Arabic names correctly.
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };
  return (
    <button type="button" className={pill} onClick={run}>
      <Download className="h-3.5 w-3.5" />
      {t('Export')}
    </button>
  );
}
